import { Link } from "react-router-dom";
import { Badge, Button, Card } from "@vb/ui";
import { Seo } from "../lib/seo.js";
import { Section, SectionHeading } from "../components/Section.js";

interface Posture {
  area: string;
  status: "shipped" | "roadmap";
  /** When status is "roadmap", the target quarter (e.g. "Q3 2026"). */
  target?: string;
  detail: string;
}

const POSTURE: Posture[] = [
  {
    area: "Provably-fair commit/reveal",
    status: "shipped",
    detail:
      "Every session commits a SHA-256 hash of a 32-byte server seed up front. Bust point is HMAC-SHA256(serverSeed, `${clientSeed}:${nonce}:bust`). After resolve, the raw seed is revealed; anyone can recompute and confirm the hash matches.",
  },
  {
    area: "RNG entropy",
    status: "shipped",
    detail:
      "@noble/hashes/utils randomBytes(32) — same primitive as Node's crypto.randomBytes — for the server seed. Cryptographically secure, unpredictable to clients, deterministic given (seed, nonce).",
  },
  {
    area: "House edge math",
    status: "shipped",
    detail:
      "Primedice CDF with 1% house edge baked in: P(crash ≥ c) = 99 / (100·c). Empirically validated by unit tests at ~99% RTP across 10k+ rounds. ~2% instabust rate (mathematical floor, not policy).",
  },
  {
    area: "Transport",
    status: "shipped",
    detail:
      "TLS only on both web (Vercel) and game-server (Fly). HSTS via Vercel default. CSP frame-ancestors 'none' + X-Frame-Options DENY across the entire site. X-Robots-Tag noindex on /admin.",
  },
  {
    area: "Operator auth",
    status: "shipped",
    detail:
      "API keys are stored as SHA-256 hashes — the plaintext is shown ONCE on creation/rotation and never persisted. Inbound X-API-Key is hashed and compared against the stored hash.",
  },
  {
    area: "Studio admin auth",
    status: "shipped",
    detail:
      "Long-random ADMIN_TOKEN (48+ hex chars) compared in constant time. Per-IP rate limit: 10 req/min on the entire /admin/* surface. Session token cleared on tab close (sessionStorage).",
  },
  {
    area: "Secrets management",
    status: "shipped",
    detail:
      "JWT_SECRET, ADMIN_TOKEN, CORS_ORIGIN are required env vars in production — the server fails to start if any is missing or under 32 chars. No insecure fallbacks ship to prod.",
  },
  {
    area: "Input validation",
    status: "shipped",
    detail:
      "Every REST request body parses through a Zod schema before reaching business logic. betAmount additionally enforced finite, > 0, ≤ MAX_BET in routes-rgs.ts as defense-in-depth.",
  },
  {
    area: "Round replay protection",
    status: "shipped",
    detail:
      "Resolve and rollback are idempotent. Re-issuing the same call returns the same outcome without double-crediting the wallet. Round phase is checked before any state mutation.",
  },
  {
    area: "Signed iframe launch tokens",
    status: "roadmap",
    target: "Q3 2026",
    detail:
      "Today operatorId is plaintext in the iframe URL. JWTs signed by the operator's API key (HMAC-SHA256, 5-min TTL) ship in the next contract revision — see /api-reference#launch-token. Until then, only the public demo operator is meant to be embedded without a backend handshake.",
  },
  {
    area: "Webhook delivery",
    status: "roadmap",
    target: "Q3 2026",
    detail:
      "Outbound webhook spec is published with HMAC-SHA256 signatures (X-VB-Signature). The delivery worker is built per first paying operator's requirements — partners can wire handlers against the spec today.",
  },
  {
    area: "On-chain seed commitments",
    status: "roadmap",
    target: "Q4 2026",
    detail:
      "The provably-fair scheme already commits to a server-seed hash before each session. Publishing that hash to Solana before the round begins makes the commitment verifiable by any third party with a block-timestamp guarantee no operator can forge — including us. Rolls out per operator opt-in.",
  },
  {
    area: "Third-party RNG / math audits",
    status: "roadmap",
    target: "Post-pilot",
    // TODO: confirm canonical org URL before launch
    detail:
      "No SOC 2, GLI, or iTechLabs certification yet. The provably-fair engine is open-source (@vb/engine, MIT) — github.com/vaultbreaker-studios/engine — and the math is re-derivable from the published seeds. That's the strongest immediate guarantee. Studios commits to commencing GLI-19 (or iTechLabs equivalent) audit within 60 days of first signed pilot. Independent audits commissioned once a paying operator's regulator requires them; budget on the post-launch roadmap.",
  },
  {
    area: "Infrastructure & sub-processors",
    status: "shipped",
    detail:
      "Two vendors carry production traffic: Vercel (static marketing site + edge routing) and Fly.io (game-server + RGS). That's the entire sub-processor list. We do not run third-party analytics or tracking beacons on this site — no Vercel Analytics, no Plausible, no GA, no pixels.",
  },
  {
    area: "Lead handling & notifications",
    status: "shipped",
    detail:
      "Lead notifications surface in the in-app admin panel — no third-party messaging service is in the loop. Contact form submissions, booking requests, and operator pings are stored in our own database and reviewed in /admin. We don't forward them to Slack, Resend, email-as-a-service, or a CRM.",
  },
];

export function Security() {
  return (
    <>
      <Seo
        title="Security · Vault Breaker Studios"
        description="Vault Breaker security posture — provably-fair flow, transport security, auth model, rate limits, and disclosure policy."
        path="/security"
      />

      <Section>
        <Badge kind="fair" style={{ marginBottom: 18 }}>
          SECURITY
        </Badge>
        <h1
          style={{
            fontFamily: "var(--font-display)",
            fontSize: "clamp(44px, 6vw, 72px)",
            lineHeight: 1,
            margin: 0,
            color: "var(--fg-1)",
            textTransform: "uppercase",
          }}
        >
          Security
        </h1>
        <p style={{ fontSize: 17, color: "var(--fg-2)", marginTop: 14, maxWidth: 720, lineHeight: 1.55 }}>
          What's locked down, what's still rolling out, and how to talk to us if something looks
          wrong. We don't custody player funds. The strongest guarantee we ship today is that
          every round is independently verifiable by anyone with the disclosed seeds.
        </p>
      </Section>

      <Section>
        <SectionHeading
          eyebrow="Posture"
          title="What we ship today"
          lead="One row per area, honest about what's done versus what's roadmap."
        />
        <Card style={{ padding: 0, overflow: "hidden" }}>
          {POSTURE.map((p, i) => (
            <PostureRow key={p.area} posture={p} first={i === 0} />
          ))}
        </Card>
      </Section>

      <Section>
        <SectionHeading
          eyebrow="Provably fair"
          title="How a player verifies a round"
          lead="Three calls, no trust required."
        />
        <Card style={{ display: "grid", gap: 10 }}>
          <ol style={{ margin: 0, paddingLeft: 20, color: "var(--fg-2)", fontSize: 14, lineHeight: 1.7 }}>
            <li>
              At session launch, save the <code>serverSeedHash</code> the server returned. This
              commits us to a specific server seed before any rounds happen.
            </li>
            <li>
              After the round resolves, fetch{" "}
              <code className="mono" style={{ color: "var(--cyan)" }}>GET /rgs/round/:id/seeds</code>{" "}
              to get the now-revealed server seed plus the (clientSeed, nonce) pair.
            </li>
            <li>
              Confirm <code>SHA256(serverSeed) === serverSeedHash</code>, then re-derive the bust
              multiplier with the engine's <code>rollRound</code> (or our hosted{" "}
              <code className="mono">/rgs/verify</code> endpoint). If the recomputed value matches
              the round you played, you got the round you were promised.
            </li>
          </ol>
        </Card>
      </Section>

      <Section>
        <SectionHeading
          eyebrow="Disclosure"
          title="Found something? Tell us first."
        />
        <Card style={{ display: "grid", gap: 12 }}>
          <p style={{ margin: 0, fontSize: 14, color: "var(--fg-2)", lineHeight: 1.6 }}>
            Email{" "}
            <a href="mailto:hello@vaultbreaker.dev" style={{ color: "var(--cyan)" }}>
              hello@vaultbreaker.dev
            </a>{" "}
            with reproduction steps. We acknowledge inside 48h, triage inside 5 business days, and
            commit to a fix inside 90 days for material findings. We don't pay bug bounties yet —
            we will credit reporters in release notes once we publish them.
          </p>
          <p style={{ margin: 0, fontSize: 13, color: "var(--fg-3)", lineHeight: 1.6 }}>
            Please don't run automated scanners against{" "}
            <code className="mono">vaultbreaker-api.fly.dev</code> — the rate limiter will lock
            your IP and we lose the signal. Manual repro only.
          </p>
        </Card>
      </Section>

      <Section tight>
        <p style={{ fontSize: 13, color: "var(--fg-3)", margin: 0 }}>
          Last updated: 2026-05-05.
        </p>
      </Section>

      <Section>
        <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
          <Link to="/api-reference" style={{ textDecoration: "none" }}>
            <Button>API REFERENCE →</Button>
          </Link>
          <Link to="/responsible-gaming" style={{ textDecoration: "none" }}>
            <Button>RESPONSIBLE GAMING →</Button>
          </Link>
          <Link to="/contact" style={{ textDecoration: "none" }}>
            <Button variant="gold">CONTACT SALES</Button>
          </Link>
        </div>
      </Section>
    </>
  );
}

function PostureRow({ posture, first }: { posture: Posture; first: boolean }) {
  const dot = posture.status === "shipped" ? "var(--green)" : "var(--gold-bright)";
  const tag =
    posture.status === "shipped"
      ? "Shipped"
      : posture.target
        ? `Roadmap · ${posture.target}`
        : "Roadmap";
  return (
    <div
      style={{
        padding: "16px 22px",
        borderTop: first ? "none" : "1px solid var(--line)",
        display: "grid",
        gridTemplateColumns: "minmax(220px, 1fr) 2fr 160px",
        gap: 16,
        alignItems: "start",
      }}
    >
      <h3 style={{ margin: 0, fontSize: 14, color: "var(--fg-1)" }}>{posture.area}</h3>
      <p style={{ margin: 0, fontSize: 13, color: "var(--fg-2)", lineHeight: 1.6 }}>
        {posture.detail}
      </p>
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          fontSize: 11,
          fontFamily: "var(--font-ui)",
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: "var(--fg-3)",
          justifySelf: "end",
          alignSelf: "start",
          marginTop: 2,
          whiteSpace: "nowrap",
        }}
      >
        <span style={{ width: 6, height: 6, borderRadius: 999, background: dot, flexShrink: 0 }} />
        {tag}
      </span>
    </div>
  );
}
